import { useState } from 'react';
import type { AdminPaymentRow } from '@st/contract';
import { useAdminPayments, useRefundPayment } from '../../api/queries';
import { QueryBoundary } from '../../components/QueryBoundary';
import { PageHeader } from '../../components/PageHeader';
import { useTranslation } from '../../i18n/I18nProvider';
import { toMutationMessage } from '../../i18n/errorMessage';
import { formatDate, formatMoney } from '../../lib/format';
import {
  Badge,
  Button,
  ConfirmDialog,
  DataTable,
  Input,
  SkeletonList,
  useToast,
  type BadgeTone,
} from '../../ui';

const TONE: Record<AdminPaymentRow['status'], BadgeTone> = {
  succeeded: 'success',
  failed: 'danger',
  refunded: 'warning',
  pending: 'info',
};

const LABEL: Record<AdminPaymentRow['status'], string> = {
  succeeded: 'admin.paid',
  failed: 'admin.failed',
  refunded: 'admin.refunded',
  pending: 'admin.pending',
};

export function AdminPayments() {
  const { t, locale } = useTranslation();
  const { toast } = useToast();
  const query = useAdminPayments();
  const refund = useRefundPayment();

  const [pending, setPending] = useState<AdminPaymentRow | null>(null);
  const [reason, setReason] = useState('');

  function close() {
    setPending(null);
    setReason('');
  }

  return (
    <>
      <PageHeader eyebrow={t('shell.admin')} title={t('admin.payments')} />
      <QueryBoundary
        query={query}
        skeleton={<SkeletonList rows={5} />}
        isEmpty={(d) => d.items.length === 0}
        empty={{ message: t('empty.noPayments') }}
      >
        {(data) => (
          <DataTable
            caption={t('admin.payments')}
            rows={data.items}
            rowKey={(r) => r.id}
            columns={[
              { key: 'user', header: t('auth.email'), render: (r) => r.userEmail },
              {
                key: 'amount',
                header: t('admin.amount'),
                align: 'right',
                render: (r) => formatMoney(r.amountCents, r.currency, locale),
              },
              {
                key: 'status',
                header: t('subscription.status'),
                render: (r) => <Badge tone={TONE[r.status]}>{t(LABEL[r.status])}</Badge>,
              },
              {
                key: 'date',
                header: t('admin.createdAt'),
                render: (r) => formatDate(r.occurredAt, locale),
                align: 'right',
                hideOnMobile: true,
              },
              {
                key: 'pi',
                header: 'Stripe',
                render: (r) => (
                  <code className="text-[12px] text-ink-muted">
                    {r.stripePaymentIntentId ?? '—'}
                  </code>
                ),
                hideOnMobile: true,
              },
              {
                key: 'refund',
                header: t('refund.action'),
                align: 'right',
                render: (r) =>
                  r.status === 'succeeded' && r.stripePaymentIntentId ? (
                    <Button size="sm" variant="ghost" onClick={() => setPending(r)}>
                      {t('refund.action')}
                    </Button>
                  ) : (
                    <span className="text-[12px] text-ink-muted">—</span>
                  ),
              },
            ]}
          />
        )}
      </QueryBoundary>

      {/* Povrat je nepovratan: modal prije poziva imenuje iznos i kupca. */}
      <ConfirmDialog
        open={pending !== null}
        title={t('refund.title')}
        question={t('refund.question', {
          amount: pending ? formatMoney(pending.amountCents, pending.currency, locale) : '',
          email: pending?.userEmail ?? '',
        })}
        confirmLabel={t('refund.confirm')}
        cancelLabel={t('common.cancel')}
        closeLabel={t('a11y.closeDialog')}
        loading={refund.isPending}
        onClose={close}
        onConfirm={() =>
          pending &&
          refund.mutate(
            { paymentId: pending.id, ...(reason.trim() ? { reason: reason.trim() } : {}) },
            {
              onSuccess: () => {
                close();
                toast(t('refund.done'), 'success');
              },
              onError: (error) => toast(toMutationMessage(t, error), 'danger'),
            },
          )
        }
      >
        <p className="text-[13px] text-ink-muted">{t('refund.warning')}</p>
        <Input
          id="refund-reason"
          label={t('refund.reason')}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </ConfirmDialog>
    </>
  );
}
